/**
 * useThreadFile — turns an attached file's id into a viewable `blob:`
 * object URL, for the chat's inline previews and the file viewer.
 *
 * Object-URL lifecycle: the URL is minted in a `useEffect` keyed on the
 * fetched blob (never in `useMemo`, which StrictMode can double-invoke
 * without a matching cleanup) and revoked in that same effect's cleanup — on
 * unmount, and whenever the blob changes (a different `fileId`, or a refetch
 * that returns a new Blob instance).
 */
import { useCallback, useEffect, useState } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { createObjectUrl, revokeObjectUrl } from '@discovr/common';

import { campaignKeys } from '../queryKeys';
import { fetchThreadFileBlob } from '../api/threadFiles';

/** Evicted from the query cache 10 minutes after its last observer unmounts
 *  — long enough to survive a quick tab-away-and-back without refetching,
 *  short enough not to pin every attachment ever opened in memory forever.
 *  `staleTime: Infinity` pairs with this: an attachment's bytes never
 *  change once uploaded, so only cache eviction ever triggers a refetch. */
const FILE_GC_MS = 10 * 60_000;

export interface UseThreadFileOptions {
  /** `false` holds the fetch, e.g. while a thumbnail is off screen. Defaults to true. */
  enabled?: boolean;
}

export interface UseThreadFileResult {
  url?: string;
  blob?: Blob;
  isLoading: boolean;
  isError: boolean;
  /** Fetches the file again, e.g. after a failed load. Does nothing while there's nothing to fetch. */
  retry(): void;
}

/** The id to fetch, or undefined while there's no id yet or the caller holds the fetch. */
function fetchableId(fileId: string | undefined, options: UseThreadFileOptions | undefined): string | undefined {
  if (!fileId) return undefined;
  if (options?.enabled === false) return undefined;
  return fileId;
}

/**
 * GET /api/v1/roster/{entry_id}/threads/content/files/{file_id}
 *
 * Fetches one attachment's bytes and exposes a `blob:` object URL for
 * inline preview/playback. Idle without a `fileId` (e.g. an attachment
 * still uploading, before the server has assigned one) or while
 * `options.enabled` is false. The download is cancelled once nothing that
 * shows the file is mounted any more (a viewer closed, a thumbnail gone).
 */
export function useThreadFile(
  entryId: string,
  fileId: string | undefined,
  options?: UseThreadFileOptions,
): UseThreadFileResult {
  const idToFetch = fetchableId(fileId, options);
  const query = useQuery({
    queryKey: campaignKeys.threadFile(entryId, fileId),
    queryFn: idToFetch ? ({ signal }) => fetchThreadFileBlob(entryId, idToFetch, signal) : skipToken,
    staleTime: Infinity,
    gcTime: FILE_GC_MS,
  });
  const { refetch } = query;

  const retry = useCallback((): void => {
    // A skipToken query can't refetch: react-query reports that as a configuration error.
    if (!idToFetch) return;
    // Resolves with the query's result; a failure shows as `isError`, not as a rejection.
    void refetch();
  }, [idToFetch, refetch]);

  const blob = query.data;
  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!blob) {
      setUrl(undefined);
      return undefined;
    }
    const objectUrl = createObjectUrl(blob);
    setUrl(objectUrl);
    return () => {
      revokeObjectUrl(objectUrl);
    };
  }, [blob]);

  return { url, blob, isLoading: query.isLoading, isError: query.isError, retry };
}
